// The HTTP boundary exposes camelCase fields (sometimes with non-enumerable
// snake_case aliases). Make the editor's known keys enumerable before any
// JSON clone. Missing fields stay missing: an unrelated edit must not save 0
// over a methodology value the editor failed to read.
const record = value => value && typeof value === 'object' && !Array.isArray(value);
const clone = value => Array.isArray(value) ? value.map(clone)
    : record(value) ? Object.fromEntries(Object.entries(value).map(([key, item]) => [key, clone(item)]))
        : value;

function aliases(value, pairs) {
    if (!record(value)) return clone(value);
    const result = clone(value);
    for (const [canonical, alternate] of pairs) {
        if (Object.prototype.hasOwnProperty.call(value, canonical)) result[canonical] = clone(value[canonical]);
        else if (Object.prototype.hasOwnProperty.call(value, alternate)) result[canonical] = clone(value[alternate]);
        delete result[alternate];
    }
    return result;
}

export function costingConfigForEditor(config) {
    if (!record(config)) return config;
    const result = aliases(config, [['pricingMethodology', 'pricing_methodology']]);
    const source = config.pricingMethodology ?? config.pricing_methodology;
    if (!record(source)) return result;
    const method = aliases(source, [
        ['default_band', 'defaultBand'], ['gst_rate', 'gstRate'],
        ['line_rounding_inc_gst', 'lineRoundingIncGst'],
        ['administration_fee_ex_gst_tiers', 'administrationFeeExGstTiers'],
        ['regulated_compliance', 'regulatedCompliance'],
        ['minimum_attendance_range_inc_gst', 'minimumAttendanceRangeIncGst'],
        ['operatorOverrides', 'operator_overrides'],
    ]);
    if (record(source.hbcf)) method.hbcf = aliases(source.hbcf, [
        ['threshold_inc_gst', 'thresholdIncGst'], ['premium_rate', 'premiumRate'],
        ['application_fee_ex_gst', 'applicationFeeExGst'],
    ]);
    if (record(method.regulated_compliance)) method.regulated_compliance = aliases(method.regulated_compliance, [
        ['engineering_srd_dcd_ex_gst', 'engineeringSrdDcdExGst'],
        ['dbpa_registration_ex_gst', 'dbpaRegistrationExGst'],
        ['fair_trading_contract_ex_gst', 'fairTradingContractExGst'],
    ]);
    if (Array.isArray(method.administration_fee_ex_gst_tiers)) {
        method.administration_fee_ex_gst_tiers = method.administration_fee_ex_gst_tiers.map(tier => aliases(tier, [
            ['max_total_inc_gst', 'maxTotalIncGst'], ['amount_ex_gst', 'amountExGst'],
        ]));
    }
    if (record(source.bands)) method.bands = aliases(source.bands, [['med_high', 'medHigh']]);
    result.pricingMethodology = method;
    return result;
}

export function numericMethodologyForSave(methodology) {
    if (!record(methodology) || methodology.id !== 'francis-sell-band-v1') return methodology;
    const method = costingConfigForEditor({ pricingMethodology: methodology }).pricingMethodology;
    const numbers = (object, keys) => {
        if (!record(object)) return object;
        const result = { ...object };
        for (const key of keys) if (Object.prototype.hasOwnProperty.call(object, key)) result[key] = Number(object[key]) || 0;
        return result;
    };
    const result = numbers(method, ['gst_rate', 'line_rounding_inc_gst']);
    if (record(method.bands)) result.bands = numbers(method.bands, ['low', 'medium', 'med_high', 'high']);
    if (record(method.hbcf)) result.hbcf = numbers(method.hbcf, ['threshold_inc_gst', 'premium_rate', 'application_fee_ex_gst']);
    if (record(method.regulated_compliance)) result.regulated_compliance = numbers(method.regulated_compliance, ['engineering_srd_dcd_ex_gst', 'dbpa_registration_ex_gst', 'fair_trading_contract_ex_gst']);
    if (Array.isArray(method.administration_fee_ex_gst_tiers)) result.administration_fee_ex_gst_tiers = method.administration_fee_ex_gst_tiers.map(tier => {
        const next = numbers(tier, ['amount_ex_gst']);
        if (Object.prototype.hasOwnProperty.call(tier, 'max_total_inc_gst')) next.max_total_inc_gst = tier.max_total_inc_gst === null || tier.max_total_inc_gst === '' ? null : Number(tier.max_total_inc_gst) || 0;
        return next;
    });
    if (Array.isArray(method.minimum_attendance_range_inc_gst)) result.minimum_attendance_range_inc_gst = method.minimum_attendance_range_inc_gst.map(value => Number(value) || 0);
    return result;
}
